/**
 * Abschnitte Stammdaten · Preise · Lager des Produkt-Editors.
 * Alle Felder des alten ProductEditModal/ProductEditPanel bleiben erhalten.
 */
import { Info, Package, Tag, Warehouse } from 'lucide-react';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { UNIT_OPTIONS, toStr } from '@/lib/productEditorModel';
import { kundenpreisValue } from '@/lib/kundenpreisEdits';
import { Section, Field, NumInput, TextInput, fmtEUR } from './EditorPrimitives';

const STOCK_STATUS = [
  { value: 'In Stock', label: 'Auf Lager' },
  { value: 'Low Stock', label: 'Kritisch' },
  { value: 'Out of Stock', label: 'Ausverkauft' },
];

export function StammdatenSection({ product, form, set, errorFields }) {
  const unitOptions = form.unit && !UNIT_OPTIONS.includes(form.unit)
    ? [form.unit, ...UNIT_OPTIONS] : UNIT_OPTIONS;
  return (
    <Section icon={Tag} title="Stammdaten"
      hint={product?.channel_shopify ? 'Titel wird beim Speichern nach Shopify übertragen; Shop-Beschreibung, SEO und Bilder werden in Shopify gepflegt.' : null}>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <Field label="Produktname *" className="sm:col-span-2" htmlFor="pe-name">
          <TextInput id="pe-name" value={form.name} invalid={errorFields.has('name')}
            onChange={(e) => set('name', e.target.value)} placeholder="z. B. Kaffeebecher 300 ml" />
        </Field>
        <Field label="Artikelnummer (SKU)" htmlFor="pe-sku" hint="Produkt-SKU; Varianten haben eigene SKUs">
          <TextInput id="pe-sku" value={form.sku} onChange={(e) => set('sku', e.target.value)} className="font-mono" placeholder="z. B. 337100" />
        </Field>
        <Field label="Kategorie" htmlFor="pe-cat">
          <TextInput id="pe-cat" value={form.category} onChange={(e) => set('category', e.target.value)} placeholder="z. B. Verpackung" />
        </Field>
        <Field label="Einheit">
          <Select value={form.unit || 'Stück'} onValueChange={(v) => set('unit', v)}>
            <SelectTrigger className="min-h-[44px]"><SelectValue /></SelectTrigger>
            <SelectContent>
              {unitOptions.map((u) => <SelectItem key={u} value={u}>{u}</SelectItem>)}
            </SelectContent>
          </Select>
        </Field>
        <Field label="Mindestbestellmenge" htmlFor="pe-moq">
          <NumInput id="pe-moq" inputMode="numeric" value={form.min_order_quantity} invalid={errorFields.has('min_order_quantity')}
            onChange={(e) => set('min_order_quantity', e.target.value)} />
        </Field>
        <Field label="Beschreibung (intern)" className="sm:col-span-2" htmlFor="pe-desc">
          <Textarea id="pe-desc" rows={3} value={form.description} onChange={(e) => set('description', e.target.value)}
            placeholder="Interne Produktinformationen…" />
        </Field>
        <Field label="Bild-URL" className="sm:col-span-2" htmlFor="pe-img" hint="Bei Shopify-Produkten kommen Bilder per Katalog-Sync.">
          <div className="flex gap-2">
            <TextInput id="pe-img" value={form.image_url} onChange={(e) => set('image_url', e.target.value)} placeholder="https://…" className="flex-1" />
            {form.image_url && (
              <img loading="lazy" decoding="async" src={form.image_url} alt="" referrerPolicy="no-referrer"
                className="w-11 h-11 rounded-lg border border-slate-200 bg-white object-contain shrink-0"
                onError={(e) => { e.currentTarget.style.visibility = 'hidden'; }} />
            )}
          </div>
        </Field>
        <div className="sm:col-span-2 flex items-center justify-between rounded-lg border border-slate-200 bg-white px-3 min-h-[44px]">
          <div>
            <p className="text-sm font-medium text-slate-900">Aktiv</p>
            <p className="text-[11px] text-slate-500">Inaktive Produkte erscheinen nicht in Bestellformular und Katalog; Historie bleibt erhalten.</p>
          </div>
          <Switch checked={!!form.is_active} onCheckedChange={(v) => set('is_active', v)} aria-label="Produkt aktiv" />
        </div>
      </div>
    </Section>
  );
}

/**
 * Kundenpreise: lesend für alle; Admin darf BESTEHENDE Preise direkt hier
 * ändern (neue Kundenpreise weiterhin nur in der Kundenkarte anlegen).
 */
function KundenpreisRow({ cp, editierbar, edits, setKundenpreis, errorIds }) {
  const wert = kundenpreisValue(cp);
  const inputId = `pe-kp-${cp.id}`;
  const geaendert = editierbar && edits[cp.id] !== undefined && edits[cp.id] !== toStr(wert);
  return (
    <div className="flex items-center justify-between gap-3 px-3 py-2 text-sm">
      <label htmlFor={editierbar ? inputId : undefined} className="truncate min-w-0 flex-1">
        {cp.lead_name || '—'}
        {geaendert && <span className="ml-1.5 text-[10px] uppercase tracking-wide text-amber-700">geändert</span>}
      </label>
      {editierbar ? (
        <NumInput id={inputId} aria-label={`Kundenpreis ${cp.lead_name || ''}`.trim()}
          value={edits[cp.id] ?? toStr(wert)} invalid={errorIds?.has(cp.id)}
          onChange={(e) => setKundenpreis(cp.id, e.target.value)}
          className="w-28 shrink-0 bg-white" placeholder="0,00" />
      ) : (
        <span className="font-semibold tabular-nums shrink-0">{fmtEUR(wert)}</span>
      )}
    </div>
  );
}

export function PreiseSection({ form, set, errorFields, kundenpreise, kundenpreiseEditierbar = false, kundenpreisEdits = {}, setKundenpreis, kundenpreisErrorIds }) {
  const editierbar = kundenpreiseEditierbar && typeof setKundenpreis === 'function';
  return (
    <Section icon={Package} title="Preise" hint="B2B-Preise und Kundenpreise verlassen FieldConnect nie. Online-Preise stehen an der Variante.">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <Field label="B2B-Standardpreis €" htmlFor="pe-preis" hint="Gilt für Varianten ohne eigenen B2B-Preis.">
          <NumInput id="pe-preis" value={form.preis} invalid={errorFields.has('preis')}
            onChange={(e) => set('preis', e.target.value)} placeholder="0,00" />
        </Field>
        <div className="space-y-1">
          <p className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">Kundenpreise € ({kundenpreise.length})</p>
          {kundenpreise.length === 0 ? (
            <p className="text-sm text-slate-400 min-h-[44px] flex items-center">Keine Kundenpreise.</p>
          ) : (
            <div className="rounded-lg border border-slate-200 bg-white divide-y divide-slate-100 max-h-64 overflow-y-auto">
              {kundenpreise.map((cp) => (
                <KundenpreisRow key={cp.id} cp={cp} editierbar={editierbar} edits={kundenpreisEdits}
                  setKundenpreis={setKundenpreis} errorIds={kundenpreisErrorIds} />
              ))}
            </div>
          )}
          <p className="text-[11px] text-slate-400 flex items-center gap-1">
            <Info className="w-3 h-3" />
            {editierbar
              ? 'Nur bestehende Kundenpreise ändern; wird mit „Speichern“ übernommen. Neue Kundenpreise in der Kundenkarte anlegen.'
              : 'Bearbeitung in der Kundenkarte.'}
          </p>
        </div>
      </div>
    </Section>
  );
}

export function LagerSection({ isNew, product, form, set, errorFields }) {
  return (
    <Section icon={Warehouse} title="Lager"
      hint={isNew ? 'Erstbestand darf hier gesetzt werden; danach nur über Wareneingang / Inventur / Warenausgang.' : 'Bestand wird ausschließlich über Lager / Inventur / Warenausgang gebucht.'}>
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <Field label={isNew ? 'Erstbestand' : 'Bestand (Ist)'} htmlFor="pe-stock">
          <NumInput id="pe-stock" inputMode="numeric" value={isNew ? form.stock : String(product?.stock ?? 0)} disabled={!isNew}
            onChange={(e) => set('stock', e.target.value)} className={!isNew ? 'bg-slate-100 font-bold text-brand-950' : ''} />
        </Field>
        <Field label="Mindestbestand" htmlFor="pe-min">
          <NumInput id="pe-min" inputMode="numeric" value={form.min_stock} invalid={errorFields.has('min_stock')} onChange={(e) => set('min_stock', e.target.value)} />
        </Field>
        <Field label="Meldebestand" htmlFor="pe-melde">
          <NumInput id="pe-melde" inputMode="numeric" value={form.melde_bestand} invalid={errorFields.has('melde_bestand')} onChange={(e) => set('melde_bestand', e.target.value)} />
        </Field>
        <Field label="Höchstbestand" htmlFor="pe-hoechst">
          <NumInput id="pe-hoechst" inputMode="numeric" value={form.hoechst_bestand} invalid={errorFields.has('hoechst_bestand')} onChange={(e) => set('hoechst_bestand', e.target.value)} />
        </Field>
        <Field label="Lagerort" className="col-span-2" htmlFor="pe-lagerort">
          <TextInput id="pe-lagerort" value={form.lagerort} onChange={(e) => set('lagerort', e.target.value)} placeholder="z. B. A-12, Regal 4" />
        </Field>
        <Field label="Lager-Status" className="col-span-2">
          <Select value={form.stock_status || 'In Stock'} onValueChange={(v) => set('stock_status', v)}>
            <SelectTrigger className="min-h-[44px]"><SelectValue /></SelectTrigger>
            <SelectContent>
              {STOCK_STATUS.map((s) => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}
            </SelectContent>
          </Select>
        </Field>
        {!isNew && (
          <p className="col-span-2 sm:col-span-4 text-[11px] text-slate-500 tabular-nums">
            Reserviert: {product?.reserved_stock ?? 0} · Verfügbar: {product?.available_stock ?? Math.max(0, (product?.stock ?? 0) - (product?.reserved_stock ?? 0))}
          </p>
        )}
      </div>
    </Section>
  );
}
